<script setup lang="ts">
const props = withDefaults(
    defineProps<{ 
        color?: `var(--color-${ string }-${ number })` | `#${ string }`
    }>(),
    { color: 'var(--color-primary-600)' }
);
const range = defineModel<number>({ default: 0 });
const inputRef = useTemplateRef('inputRef');
const tooltipRef = useTemplateRef('tooltipRef');

const progressWidth = computed(() => {
    if (!inputRef.value) return 0;

    const thumbWidth = 32;
    const min = +inputRef.value.min || 0;
    const max = +inputRef.value.max || 100;
    const inputWidth = inputRef.value.clientWidth;

    const percent = Math.max(0, Math.min(1, (range.value - min) / (max - min)));
    
    return (thumbWidth / 2) + percent * (inputWidth - thumbWidth);
});

const tooltipRight = computed(() => {
    if (!tooltipRef.value) return 0;
    const tooltipWidth = tooltipRef.value.offsetWidth;

    return progressWidth.value - (tooltipWidth / 2);
});
</script>

<template>
    <div class="relative flex-center">
        <input 
            ref="inputRef"
            v-bind="$attrs"
            v-model.number="range"
            type="range" 
            class="w-full"
        >
        <div 
            class="absolute h-3 rounded-full right-0.5 pointer-events-none"
            :style="{ 
                width: `${ progressWidth }px`,
                backgroundColor: props.color,
            }" 
        />
        <div
            ref="tooltipRef"
            class="w-max min-w-8 absolute -top-10 bg-white flex-center rounded-sm px-2 py-1 shadow-primary-xs select-none
                before:content-[''] before:absolute before:w-2 before:h-2 before:bg-white
                before:rotate-45 before:-bottom-1"
            :style="{ right: `${ tooltipRight }px` }"
        >
            <span class="text-body2-semibold text-gray-500">{{ range }}</span>
        </div>
    </div>
</template>

<style scoped>
@reference "~/assets/css/main.css";

input[type="range"] {
    appearance: none;
    @apply
    w-full outline-0 border-0 p-0 m-0 shadow-none
}
input[type="range"]::-webkit-slider-runnable-track {
    appearance: none;
    @apply
    w-full h-4 bg-white cursor-pointer rounded-full border-2 border-primary-100
}
input[type="range"]::-webkit-slider-thumb {
    appearance: none;
    background-color: v-bind('props.color');
    @apply
    w-8 aspect-square rounded-full border-5 border-white -mt-2.5 relative z-1
}
input[type="range"]:hover::-webkit-slider-thumb {
    @apply
    outline-4 outline-black/10
}
</style>